import { ClientStateRoot } from './schema';
import {
  ShopifyPageType,
  ShopifyInfo,
  ShopifyProductInfo,
  ShopifyCollectionInfo,
  IShopifyVariant,
} from './schema/shopifyinfo';

// window properties
const WINDOW_SHOPIFY_PROPERTY = 'Shopify';
const WINDOW_ANALYTICS_PROPERTY = 'ShopifyAnalytics';
// URL paths
const PATH_COLLECTIONS = '/collections';
const PATH_CART = '/cart';
const PATH_LOGIN = '/account/login';
const PATH_ACCOUNT = '/account';
// ShopifyAnalytics.meta.page.path values
const PATH_CHECKOUT_CONTACT_INFORMATION = '/checkout/contact_information';
const PATH_CHECKOUT_PAYMENT = '/checkout/payment';
const PATH_CHECKOUT_THANK_YOU = '/checkout/thank_you';
// ShopifyAnalytics.meta.page.pageType values
const PAGE_TYPE_COLLECTION = 'collection';
const PAGE_TYPE_PRODUCT = 'product';
const PAGE_TYPE_HOME = 'home';
const PAGE_TYPE_SEARCHRESULTS = 'searchresults';

// Example shop to test this form type: https://hipperhiker.myshopify.com/products/brown-leather-mens-watch-custom-handle?variant=32219863613506
const QUERY_SELECTOR_FORM1 = 'form[action^="/cart/add"]';
const QUERY_SELECTOR_FORM2 = `${QUERY_SELECTOR_FORM1}[data-delm-is-primary-form="true"]`;
const QUERY_SELECTOR_VARIANT = '[name="id"]';

// === Typing for shopify window properties we use ===
interface IShopify {
  locale?: string; // eg: "en"
  shop?: string; // eg: "electric-rose-clothing.myshopify.com"
  currency?: {
    active?: string; // eg: "USD"
    rate?: string; // eg: "1.0"
  };
}

interface IShopifyAnalytics {
  meta?: {
    selectedVariantId?: string;
    product?: Partial<{
      id: number;
      variants: Array<Partial<IShopifyVariant>>;
      type: string;
      vendor: string;
    }>;
    currency?: string;
    page?: Partial<{
      pageType: string;
      resourceId: number;
      path: string;
    }>;
  };
}

const getPageType = (
  pathname?: string,
  ShopifyAnalytics?: IShopifyAnalytics
): ShopifyPageType | undefined => {
  const pageType: string | undefined = ShopifyAnalytics?.meta?.page?.pageType;
  const pagePath = ShopifyAnalytics?.meta?.page?.path;
  if (pageType === PAGE_TYPE_COLLECTION) {
    return ShopifyPageType.COLLECTION;
  }
  if (pageType === PAGE_TYPE_PRODUCT) {
    return ShopifyPageType.PRODUCT;
  }
  if (pageType === PAGE_TYPE_HOME) {
    return ShopifyPageType.HOME;
  }
  if (pageType === PAGE_TYPE_HOME) {
    return ShopifyPageType.HOME;
  }
  if (pageType === PAGE_TYPE_SEARCHRESULTS) {
    return ShopifyPageType.SEARCHRESULTS;
  }
  if (pagePath === PATH_CHECKOUT_CONTACT_INFORMATION) {
    return ShopifyPageType.CHECKOUT_CONTACT_INFORMATION;
  }
  if (pagePath === PATH_CHECKOUT_PAYMENT) {
    return ShopifyPageType.CHECKOUT_PAYMENT;
  }
  if (pagePath === PATH_CHECKOUT_THANK_YOU) {
    return ShopifyPageType.CHECKOUT_THANK_YOU;
  }
  if (pathname === PATH_CART) {
    return ShopifyPageType.CART;
  }
  if (pathname === PATH_LOGIN) {
    return ShopifyPageType.LOGIN;
  }
  if (pathname === PATH_ACCOUNT) {
    return ShopifyPageType.ACCOUNT;
  }
};

const shopifyDetected = (window: Partial<Window>) => {
  return WINDOW_ANALYTICS_PROPERTY in window && WINDOW_SHOPIFY_PROPERTY in window;
};

const getQueryParam = (win: Partial<Window>, name: string) => {
  name = name.replace(/[\[\]]/g, '\\$&');
  var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(win?.location?.search || '');
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

const selectVariant = (
  variantId?: string,
  variants?: Partial<IShopifyVariant>[]
): Partial<IShopifyVariant> | undefined => {
  if (variantId && variants) {
    const parsedId = parseInt(variantId, 10);
    const variant = variants.find(v => v.id === parsedId);
    if (variant) {
      variant.variantId = parsedId;
      return variant;
    }
  }
};

// Thanks, Jopi!
const getProductInfo = (
  window: Partial<Window>,
  ShopifyAnalytics?: IShopifyAnalytics
): ShopifyProductInfo['product'] => {
  const product: ShopifyProductInfo['product'] = {
    productId: ShopifyAnalytics?.meta?.product?.id,
    type: ShopifyAnalytics?.meta?.product?.type,
    vendor: ShopifyAnalytics?.meta?.product?.vendor,
  };

  // If there is only a single variant of the product, we're done, just use that.
  if (ShopifyAnalytics?.meta?.product?.variants?.length === 1) {
    const onlyVariant = ShopifyAnalytics?.meta?.product?.variants?.[0];
    product.variant = {...onlyVariant, variantId: onlyVariant?.id};
    return product;
  }

  // if there is a selectedVariantId and it points to a valid variant, use that.
  // sometimes selectedVariantId points to a non-existent variant
  if (typeof ShopifyAnalytics?.meta?.selectedVariantId === 'string') {
    product.variant = selectVariant(
      ShopifyAnalytics?.meta?.selectedVariantId,
      ShopifyAnalytics?.meta?.product?.variants
    );
    if (product.variant) {
      // selectedVariantId points to an existing variant, so we're done
      return product;
    }
  }

  // try to dig variantId out of the DOM
  if (window.document) {
    const doc = window.document;

    var addToCartForm =
      doc.querySelector(QUERY_SELECTOR_FORM2) || doc.querySelector(QUERY_SELECTOR_FORM1);
    if (addToCartForm) {
      var variantSelect = addToCartForm.querySelector(QUERY_SELECTOR_VARIANT);
      if (variantSelect instanceof HTMLSelectElement || variantSelect instanceof HTMLInputElement) {
        product.variant = selectVariant(
          variantSelect.value,
          ShopifyAnalytics?.meta?.product?.variants
        );
        if (product.variant) {
          return product;
        }
      }
    }
  }
  // Next, try to get the variant id from the URL query param
  var urlVariantId = getQueryParam(window, 'variant');
  if (urlVariantId) {
    product.variant = selectVariant(urlVariantId, ShopifyAnalytics?.meta?.product?.variants);
    if (product.variant) {
      return product;
    }
  }

  return product;
};

const getCollectionInfo = (
  window: Partial<Window>,
  ShopifyAnalytics: IShopifyAnalytics
): ShopifyCollectionInfo['collection'] => {
  let collectionHandle;
  const path = window?.location?.pathname;
  if (path) {
    const ix = path.indexOf(PATH_COLLECTIONS);
    if (ix > -1) {
      collectionHandle = path.substr(ix + 1 + PATH_COLLECTIONS.length);
    }
  }
  return {
    collectionHandle,
    collectionId: ShopifyAnalytics.meta?.page?.resourceId,
  };
};

export const extendClientStateRoot = (
  root: ClientStateRoot,
  window: Partial<Window>
): ClientStateRoot => {
  if (shopifyDetected(window)) {
    let shopifyInfo: ShopifyInfo | undefined = undefined;
    const Shopify = (window as any)[WINDOW_SHOPIFY_PROPERTY] as IShopify;
    const ShopifyAnalytics = ((window as any)[WINDOW_ANALYTICS_PROPERTY] ||
      {}) as IShopifyAnalytics;
    const pageType = getPageType(window?.location?.pathname, ShopifyAnalytics);
    const shopInfo = {
      locale: Shopify?.locale,
      currency: Shopify?.currency?.active,
      shopId: Shopify?.shop,
    };
    switch (pageType) {
      case ShopifyPageType.PRODUCT:
        shopifyInfo = {
          pageType,
          product: getProductInfo(window, ShopifyAnalytics),
          ...shopInfo,
        };
        break;
      case ShopifyPageType.COLLECTION:
        shopifyInfo = {
          pageType,
          collection: getCollectionInfo(window, ShopifyAnalytics),
          ...shopInfo,
        };
        break;
      case undefined:
        break;
      default:
        shopifyInfo = { pageType, ...shopInfo };
    }
    if (shopifyInfo !== undefined) {
      root.shopify = shopifyInfo;
    }
  }
  return root;
};
